// @ts-check
import React, { StatelessComponent } from 'react'; // eslint-disable-line no-unused-vars
import PropTypes from 'prop-types';
import cx from 'classnames';
import { connect, Dispatch } from 'react-redux'; // eslint-disable-line no-unused-vars
import { reduxForm } from 'utils/reduxFormWrapper';
import { submit, InjectedFormProps } from 'redux-form'; // eslint-disable-line no-unused-vars
import Icon from 'shared/components/Icon';
import { referralDashboardSearchSubmitted } from 'shared/actions/ReferralProfiles/ReferralDashboardActions';
import {
  TextInput,
  PlainSelectInput,
  SimpleCheckboxField,
} from 'shared/containers/portal_layout/HForm';
import SpinnerButton from 'shared/components/SpinnerButton';
import { Specialty } from '../../types'; // eslint-disable-line no-unused-vars
import styles from './ResultSearchForm.module.scss';

const rangeOptions = [
  { value: '5', label: '5 miles' },
  { value: '15', label: '15 miles' },
  { value: '30', label: '30 miles' },
  { value: '60', label: '60 miles' },
  { value: '90', label: '90 miles' },
  { value: '250', label: '250 miles' },
];

/** @typedef {{
  specialtyId: string,
  isPediatric: boolean,
  searchTerm: string,
  range: string
}} ResultSearchFormType */

/** @typedef {{
  referrableSpecialties: Specialty[],
  filterByDistance: boolean,
  showSpecialtySelect: boolean,
  referralProfileId: string,
  appliedSearchTerm?: string,
  isFetching: boolean,
  handleSubmit: (formValues: ResultSearchFormType) => Promise,
  dispatch: Dispatch<any>
} & InjectedFormProps<ResultSearchFormType> } ResultControlsPropsType */

/** @type { React.ValidationMap<ResultControlsPropsType> } */
const propTypes = {
  referrableSpecialties: PropTypes.array.isRequired,
  filterByDistance: PropTypes.bool.isRequired,
  showSpecialtySelect: PropTypes.bool.isRequired,
  referralProfileId: PropTypes.string.isRequired,
  appliedSearchTerm: PropTypes.string,
  isFetching: PropTypes.bool.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  submitting: PropTypes.bool.isRequired,
  change: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
};

/** @type { StatelessComponent<ResultControlsPropsType> } */
const ResultControls = ({
  referrableSpecialties,
  filterByDistance,
  showSpecialtySelect,
  referralProfileId,
  appliedSearchTerm,
  isFetching,
  handleSubmit,
  submitting,
  change,
  dispatch,
}) => (
  <form className={styles.search_container} onSubmit={handleSubmit} data-spec-id="ResultSearchForm">
    {showSpecialtySelect ? (
      <div>
        <label className={styles.section_label}>
          <span className={styles.icon_container}>
            <Icon name="stethoscope" />
          </span>{' '}
          Specialty
        </label>
        <div className={styles.section_controls}>
          <div className={styles.select_container}>
            <PlainSelectInput
              id="specialtySelect"
              className="select"
              style={{ fontSize: 14, width: 220 }}
              options={referrableSpecialties}
              labelKey="name"
              valueKey="id"
              name="specialtyId"
            />
            <div>
              <SimpleCheckboxField
                className={styles.pediatric_checkbox_label}
                name="isPediatric"
                label="Pediatric"
              />
            </div>
          </div>
        </div>
      </div>
    ) : null}

    <div className={styles.section_bordered}>
      <label className={styles.section_label}>
        <span className={styles.icon_container}>
          <Icon name="search" />
        </span>{' '}
        Search Term
        {appliedSearchTerm ? (
          <span
            className={styles.clear_search_term_link}
            onClick={() => {
              change('searchTerm', '');
              return setTimeout(() => dispatch(submit('referralResultSearch')), 0);
            }}
          >
            clear
          </span>
        ) : null}
      </label>
      <div className={styles.section_controls}>
        <div className={styles.input_container}>
          <TextInput
            className={styles.input}
            type="text"
            placeholder="All Providers/Practices"
            name="searchTerm"
          />
        </div>
      </div>
    </div>

    <div>
      <label className={styles.section_label}>
        <span className={styles.icon_container}>
          <Icon name="location" />
        </span>{' '}
        Distance
      </label>
      <div className={styles.section_controls}>
        <div className={styles.select_container}>
          {filterByDistance ? (
            <PlainSelectInput
              prompt={false}
              className={cx('select', styles.distance_select)}
              style={{ fontSize: 14 }}
              options={rangeOptions}
              name="range"
            />
          ) : (
            <span>All Locations</span>
          )}
        </div>
      </div>
    </div>

    <div className={styles.submit_section}>
      <SpinnerButton
        disabled={isFetching}
        type="submit"
        className="button is-link"
        loading={submitting}
      >
        Search
      </SpinnerButton>
    </div>
  </form>
);

ResultControls.displayName = 'ResultControls';
ResultControls.propTypes = propTypes;

function onSubmit(formValues, dispatch, ownProps) {
  dispatch(
    referralDashboardSearchSubmitted(
      ownProps.referralProfileId,
      formValues.searchTerm,
      formValues.specialtyId,
      formValues.range,
      formValues.isPediatric ? 'pediatric' : 'adult',
      ownProps.requestedNetworkId
    )
  );
}

const ResultControlsForm = reduxForm({
  form: 'referralResultSearch',
  onSubmit,
})(ResultControls);

function mapStateToProps(state) {
  const { pageState } = state.referralDashboardResults;
  return {
    requestedNetworkId: pageState.network.id,
    initialValues: {
      specialtyId: pageState.specialtyId, // expected to be set by parent before mounting this
      range: state.referralDashboardResults.pageState.range,
      isPediatric: pageState.ageClassification === 'pediatric', // expected to be set by parent before mounting this
    },
    appliedSearchTerm: pageState.searchTerm,
  };
}

export default connect(mapStateToProps)(ResultControlsForm);
